import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import PerformerCard from './PerformerCard';
import RoomSelector from '../../../shared/RoomSelector';

import { Button, Modal, Select, TextInput } from '@opendate-app/setlist';

import ArtistPickerField from '../../shared/components/ArtistPickerField';

import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

var _ = require('lodash');
var debouncedAutoSaveConfirm;

import {
  DETAILS_MENU_ITEM
} from '../constants/confirmTicketingConstants';
import useVenueAgeRestrictionOptions from '../../shared/hooks/useVenueAgeRestrictionOptions';
import withSavedOption from '../../shared/utils/withSavedOption';

const BasicInfo = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  activeMenuItemChanged,
  updateConfirm,
  autoSaveConfirm,
  venuesOptions,
  rooms,
  venueOwnerships,
  performers,
  deletePerformer,
  createPerformer,
  updatePerformer,
  performerDragged,
  admissionSeatingChartEvent,
  addOnSeatingChartEvent
}) => {
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [pendingVenueChange, setPendingVenueChange] = useState(null);

  const applyRoomChange = (selected, updated) => {
    setSelectedRooms(selected || []);
    updateConfirm(updated);
    debouncedAutoSaveConfirm(csrfToken, team, updated);
  };
  
  useEffect(() => {
    debouncedAutoSaveConfirm = _.debounce((csrfToken, team, confirm) => {
      autoSaveConfirm(csrfToken, team, confirm);
    }, 600);
  }, [])

  useEffect(() => {
    if (confirm && confirm.room_ids && rooms && venueOwnerships) {
      const roomOptions = confirm.room_ids.map(roomId => {
        const roomData = rooms.find(r => r.id === roomId);
        if (roomData) {
          return {
            value: roomData.id,
            label: roomData.name,
            room: roomData,
            venueOwnershipId: roomData.venue_ownership_id
          };
        }
        return null;
      }).filter(Boolean);
      setSelectedRooms(roomOptions);
    }
  }, [confirm, rooms, venueOwnerships])

  const ageRestrictionVenueOwnershipId = selectedRooms[0]?.venueOwnershipId;
  const { options: ageRestrictionOptions } =
    useVenueAgeRestrictionOptions(team?.id, ageRestrictionVenueOwnershipId);
  const ageRestrictionSelectOptions = withSavedOption(ageRestrictionOptions, confirm.age_restriction);

  return (
    <div className="row">
      <div className="col-12">
        <h3 style={{"fontSize": "20px"}}>
          <strong>
            Artists & Details
          </strong>
        </h3>
        <p className="pb-2">
          Add artists to your lineup. Opendate will automatically add pics, bios, and more to your event page. You can also add your own photos and customize the copy as needed for your event.
        </p>
        <form onSubmit={
                (e) => {
                  e.preventDefault();
                }
              }>
          <div className="form-group mt-4">
            <ArtistPickerField
              placeholder="Add Artist"
              description="Search your CRM, or add a new artist inline."
              teamId={team.id}
              onSelect={(sel) => createPerformer(csrfToken, team, confirm, { value: sel.artistId })}
            />
          </div>
          <div>
            <DragDropContext onDragEnd={
                               (result) => {
                                 if (!result.destination) {
                                   return false;
                                 }

                                 var itemIndex = result.source.index;
                                 var moveIndex = result.destination.index;
                                 var updated = Object.assign({}, performers[itemIndex], {
                                   position: (moveIndex + 1)
                                 });

                                 performerDragged(result);
                                 updatePerformer(csrfToken, team, updated);
                               }
                             }>
              <Droppable droppableId="performers-droppable">
                {(provided, snapshot) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {performers.sort((a, b) => a.position - b.position).map((performer, index) =>
                      <Draggable key={"" + performer.id} draggableId={"" + performer.id} index={index}>
                        {(provided, snapshot) => (
                          <PerformerCard key={index}
                                         csrfToken={csrfToken}
                                         team={team}
                                         currentUser={currentUser}
                                         confirm={confirm}
                                         performer={performer}
                                         deletePerformer={deletePerformer}
                                         updatePerformer={updatePerformer}
                                         provided={provided}
                                         snapshot={snapshot} />
                        )}
                      </Draggable>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="form-group mt-4 pt-2">
            <TextInput label="Event Title"
                   value={confirm.title || ""}
                   onChange={
                     (e) => {
                       var newTitle = e.target.value;

                       if (newTitle.length === 0){
                         var performersWithArtists = performers.filter((p) => p.artistable);

                         if (performersWithArtists.length > 0){
                           newTitle = performersWithArtists.map((p) => p.artistable.name).join(" & ");
                         } else {
                           newTitle = "Untitled Event";
                         }
                       }

                       var updated = Object.assign({}, confirm, {
                         title: newTitle
                       });

                       updateConfirm(updated);
                       debouncedAutoSaveConfirm(csrfToken, team, updated);
                     }
                   }
                   id="confirm-title" />
          </div>
          <div className="form-group">
            <TextInput label="Presenter"
                   value={confirm.presenter || ""}
                   onChange={
                     (e) => {
                       var updated = Object.assign({}, confirm, {presenter: e.target.value});

                       updateConfirm(updated);
                       debouncedAutoSaveConfirm(csrfToken, team, updated);
                     }
                   }
                   id="confirm-presenter" />
          </div>
          <hr className="my-5"
              style={{"borderTop": "1px solid var(--sl-border-secondary)"}} />
          <h3 style={{"fontSize": "20px"}}>
            <strong>
              Venue
            </strong>
          </h3>
          <p className="pb-2">Confirm show venue and adjust show capacity if applicable.</p>
          <div className="form-row mt-3">
            <div className="col-12 col-md-8 mb-3 mb-md-0">
              <label htmlFor="confirm-rooms"
                     className="mb-1"
                     style={{"fontSize": "14px", "color": "var(--sl-text-secondary)"}}>
                Venue
              </label>
              <RoomSelector
                rooms={rooms}
                venueOwnerships={venueOwnerships}
                selectedRooms={selectedRooms}
                id="confirm-rooms"
                hasError={selectedRooms.length === 0}
                isDisabled={!confirm.can_change_ticketing_arrangement}
                onChange={(selected) => {
                  if (!confirm.can_change_ticketing_arrangement) {
                    return;
                  }

                  const roomIds = (selected || []).map(room => room.value);
                  let updated = Object.assign({}, confirm, {
                    room_ids: roomIds
                  });

                  if (selected && selected.length > 0) {
                    const firstRoom = selected[0];
                    const venueOwnership = venueOwnerships.find(vo => vo.id === firstRoom.venueOwnershipId);
                    if (venueOwnership && venueOwnership.venueable_type === "Venue") {
                      const venue = venuesOptions.find(v => v.id === venueOwnership.venueable_id);
                      if (venue) {
                        updated = Object.assign(updated, {
                          venue: venue,
                          venue_capacity: venue.capacity,
                          age_restriction: venue.confirm_age_restriction,
                          venue_ownership_id: venue.venue_ownership_id
                        });
                      }
                    }
                  }

                  const hasSeatMap = (confirm.ticketing_arrangement === "Seated" && admissionSeatingChartEvent?.seating_chart_id) || addOnSeatingChartEvent?.seating_chart_id;
                  const currentVenueOwnershipId = selectedRooms[0]?.venueOwnershipId ?? confirm.venue_ownership_id;
                  const wipesSeatMap = hasSeatMap && selected?.length > 0 && selected[0].venueOwnershipId !== currentVenueOwnershipId;
                  if (wipesSeatMap) return setPendingVenueChange({ selected, updated });
                  applyRoomChange(selected, updated);
                }}
              />
              <Modal
                open={pendingVenueChange !== null}
                onClose={() => setPendingVenueChange(null)}
                type="warning"
                role="alertdialog"
                width={440}
                title="Change the venue?"
                description="This removes the seat map and its seated tickets, prices, add-ons, holds and kills. You'll need to pick a map again."
                footer={(
                  <>
                    <Button variant="secondary" autoFocus onClick={() => setPendingVenueChange(null)}>Cancel</Button>
                    <Button variant="primary" onClick={() => { applyRoomChange(pendingVenueChange.selected, pendingVenueChange.updated); setPendingVenueChange(null); }}>Change venue</Button>
                  </>
                )}
              />
            </div>
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <div className="form-group mb-0">
                <TextInput as={MaskedInput}
                             label="Capacity"
                             mask={createNumberMask({
                               prefix: "",
                               decimalLimit: 0
                             })}
                             placeholder={"0"}
                             id="confirm-venue-capacity"
                             onChange={
                               (e) => {
                                 var updated = Object.assign({}, confirm, {venue_capacity: e.target.value});

                                 updateConfirm(updated);
                                 debouncedAutoSaveConfirm(csrfToken, team, updated);
                               }
                             }
                             value={confirm.venue_capacity || ""} />
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="col-12 col-md-4">
              <Select
                id="confirm-age-restriction"
                label="Age Restriction"
                clearable
                options={ageRestrictionSelectOptions}
                value={confirm.age_restriction || null}
                onChange={(ageRestriction) => {
                  var updated = Object.assign({}, confirm, {age_restriction: ageRestriction || null});

                  updateConfirm(updated);
                  debouncedAutoSaveConfirm(csrfToken, team, updated);
                }}
              />
            </div>
          </div>
        </form>
        <div className="row">
          <div className="col-12 d-flex justify-content-end align-items-end"
               style={{"height": "130px"}}>
            <Button variant="primary"
                    onClick={() => activeMenuItemChanged(DETAILS_MENU_ITEM)}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

BasicInfo.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  activeMenuItemChanged: PropTypes.func.isRequired,
  updateConfirm: PropTypes.func.isRequired,
  autoSaveConfirm: PropTypes.func.isRequired,
  venuesOptions: PropTypes.array,
  rooms: PropTypes.array,
  venueOwnerships: PropTypes.array,
  performers: PropTypes.array,
  deletePerformer: PropTypes.func.isRequired,
  createPerformer: PropTypes.func.isRequired,
  updatePerformer: PropTypes.func.isRequired,
  performerDragged: PropTypes.func.isRequired,
  admissionSeatingChartEvent: PropTypes.object,
  addOnSeatingChartEvent: PropTypes.object
};

BasicInfo.defaultProps = {
  admissionSeatingChartEvent: null,
  addOnSeatingChartEvent: null
};

export default BasicInfo;
